import { useMemo, type ReactNode } from 'react';
import { api } from '../../lib/api';
import { useResource } from '../../lib/useResource';
import { Donut } from '../common';
import type { ActivityHeatCell, ActivityLoginPoint, ActivityWindow } from '@bbbk/schema';
import { AccountSectionBars } from './AccountSectionBars';
import { AccountLoginsLine } from './AccountLoginsLine';
import { sectionTotals, loginsSeries } from './accountCharts';
import { WINDOW_LABEL } from './activityFormat';
import { roleCounts } from './filter';
import { AccountError } from './tabUi';
import type { User } from './types';

// The three chart cards above the toolbar — the page's stat row, stable across
// the Feed and List tabs. Each is a .fin-panel (the Finances/Bookings card), in
// this order:
//   1. Sign-ins per day (line) — the /activity/logins timeseries, windowed.
//   2. Page views by section (bar) — the heatmap summed across the team, windowed.
//   3. Team by role (donut) — the allowlist split admin/owner/helper/staff. NOT
//      windowed: it is a live snapshot of who has access right now.
// Page-view + login reads are fire-and-forget ingested, so a write must NOT
// refetch them (invalidatedBy: []); the roles donut reads the same `users` the
// page already holds — no extra fetch.
//
// Every card carries a `meta` naming its own period, because the three are not
// all the same read and a card that states nothing is taken for a lifetime
// total. Two say the window they queried; the donut says it has no window.

function ChartCard({ title, meta, loading, error, children }: { title: string; meta: string; loading: boolean; error: boolean; children: ReactNode }) {
  return (
    <div className="fin-panel account-chart-card">
      <div className="fin-panel-head"><h3>{title}</h3><span className="panel-meta">{meta}</span></div>
      {error ? (
        <AccountError message="Couldn’t load this chart. Try again." />
      ) : loading ? (
        <div className="account-chart-skeleton" aria-busy="true" role="status" aria-label={`Loading ${title}`} />
      ) : (
        children
      )}
    </div>
  );
}

export function AccountChartCards({ window, users }: { window: ActivityWindow; users: User[] | null }) {
  const heat = useResource(
    `activity:heatmap:${window}`,
    () => api<ActivityHeatCell[]>(`/activity/heatmap?window=${window}`).then((r) => r.data),
    { invalidatedBy: [] },
  );
  const logins = useResource(
    `activity:logins:${window}`,
    () => api<ActivityLoginPoint[]>(`/activity/logins?window=${window}`).then((r) => r.data),
    { invalidatedBy: [] },
  );

  const sections = useMemo(() => sectionTotals(heat.state.data ?? []), [heat.state.data]);
  const series = useMemo(() => loginsSeries(logins.state.data ?? [], window), [logins.state.data, window]);
  // The roles split reads the allowlist the page already holds — no extra fetch.
  const roles = useMemo(() => roleCounts(users ?? []), [users]);

  const pending = (data: unknown, phase: string) => data == null && phase !== 'error';
  // The period both event-backed cards queried — one value, so the two heads
  // can never disagree with each other or with the data they charted.
  const period = WINDOW_LABEL[window];

  return (
    <div className="account-charts">
      <ChartCard title="Sign-ins per day" meta={period} loading={pending(logins.state.data, logins.state.phase)} error={logins.state.phase === 'error'}>
        <AccountLoginsLine points={series} window={window} />
      </ChartCard>
      <ChartCard title="Page views by section" meta={period} loading={pending(heat.state.data, heat.state.phase)} error={heat.state.phase === 'error'}>
        <AccountSectionBars data={sections} window={window} />
      </ChartCard>
      {/* No window: the donut counts the allowlist as it stands, so it must not
          borrow the other two cards' period. */}
      <ChartCard title="Team by role" meta="Current access" loading={users == null} error={false}>
        <Donut
          title="Team by role"
          showTitle={false}
          centerLabel="users"
          segments={[
            { label: 'Admins', value: roles.admin, color: 'var(--brand-deep)' },
            { label: 'Owners', value: roles.owner, color: 'var(--brand)' },
            { label: 'Helpers', value: roles.helper, color: 'var(--secondary)' },
            { label: 'Staff', value: roles.staff, color: 'var(--ink-mute)' },
          ]}
        />
      </ChartCard>
    </div>
  );
}
